import { useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import Modal from "../components/Modal";
import PdfPageCanvas from "../components/PdfPageCanvas";
import usePdfPageCount from "../hooks/usePdfPageCount";
import cvData from "../data/cv";

const CV_URL = `${process.env.PUBLIC_URL}/cv/${cvData.file}`;

export default function CV() {
  const numPages = usePdfPageCount(CV_URL);
  const [activePage, setActivePage] = useState(null);

  const pages = numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : [];

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pb-14 pt-32 md:px-12 md:pb-20 md:pt-40">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Curriculum Vitae</p>
            <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
              My CV
            </h1>
            <p className="mt-5 max-w-[52ch] text-[0.95rem] leading-relaxed text-ink-soft">
              This is the CV I actually send out, kept up to date so I can point people here instead of
              digging up a file. Click any page to read it full-size, or take the PDF with you.
            </p>
          </div>
          <a
            href={CV_URL}
            download
            className="flex-shrink-0 border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            &darr; Download PDF
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-content px-6 pb-24 md:px-12">
        {numPages === null && <p className="mb-6 text-sm text-ink-faint">Loading CV&hellip;</p>}
        {numPages === 0 && <p className="mb-6 text-sm text-ink-faint">Couldn&rsquo;t load the CV file.</p>}

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {pages.map((pageNumber, i) => (
            <Reveal
              as="button"
              key={pageNumber}
              delay={i * 70}
              onClick={() => setActivePage(pageNumber)}
              className="flex flex-col items-center gap-3 border border-line p-4 transition-colors hover:border-line-strong hover:bg-surface-alt"
            >
              <PdfPageCanvas url={CV_URL} pageNumber={pageNumber} width={340} />
              <span className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint">
                Page {pageNumber} of {numPages}
              </span>
            </Reveal>
          ))}
        </div>
      </div>

      <Modal
        open={activePage !== null}
        onClose={() => setActivePage(null)}
        title={activePage ? `${cvData.title}, page ${activePage} of ${numPages}` : ""}
        footer={
          <>
            {numPages > 1 && (
              <div className="mr-auto flex gap-3">
                <button
                  className="border border-line-strong px-4 py-2 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading disabled:opacity-30"
                  disabled={activePage <= 1}
                  onClick={() => setActivePage((p) => Math.max(1, p - 1))}
                >
                  &larr; Prev
                </button>
                <button
                  className="border border-line-strong px-4 py-2 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading disabled:opacity-30"
                  disabled={activePage >= numPages}
                  onClick={() => setActivePage((p) => Math.min(numPages, p + 1))}
                >
                  Next &rarr;
                </button>
              </div>
            )}
            <a
              href={CV_URL}
              download
              className="border border-forest-900 bg-forest-900 px-6 py-2.5 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
            >
              &darr; Download PDF
            </a>
          </>
        }
      >
        {activePage !== null && <PdfPageCanvas url={CV_URL} pageNumber={activePage} width={780} />}
      </Modal>

      <Footer />
    </>
  );
}
